import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

// Mirrors AREA_DATA on the backend so links stay consistent with sitemap
export const AREAS = [
  ['baner', 'Baner'], ['balewadi', 'Balewadi'], ['aundh', 'Aundh'], ['kothrud', 'Kothrud'],
  ['koregaon-park', 'Koregaon Park'], ['koregaon-park-annexe', 'Koregaon Park Annexe'],
  ['kharadi', 'Kharadi'], ['wagholi', 'Wagholi'], ['viman-nagar', 'Viman Nagar'],
  ['lohegaon', 'Lohegaon'], ['hinjewadi', 'Hinjewadi'], ['mahalunge', 'Mahalunge'],
  ['bavdhan', 'Bavdhan'], ['wakad', 'Wakad'], ['punawale', 'Punawale'],
  ['tathawade', 'Tathawade'], ['ravet', 'Ravet'], ['kalyani-nagar', 'Kalyani Nagar'],
  ['wadgaon-sheri', 'Wadgaon Sheri'], ['hadapsar', 'Hadapsar'], ['magarpatta', 'Magarpatta'],
  ['mundhwa', 'Mundhwa'], ['nibm-road', 'NIBM Road'], ['senapati-bapat-road', 'SB Road'],
  ['sinhagad-road', 'Sinhagad Road'], ['camp', 'Camp / MG Road'],
  ['pimple-saudagar', 'Pimple Saudagar'], ['pimple-nilakh', 'Pimple Nilakh'],
  ['fc-road', 'FC Road'], ['law-college-road', 'Law College Road'],
  ['navi-mumbai', 'Navi Mumbai'], ['mumbai-pune-highway', 'Mumbai-Pune Highway'],
  ['fatima-nagar', 'Fatima Nagar'], ['sopanbaug', 'Sopanbaug'],
  ['salisbury-park', 'Salisbury Park'], ['market-yard', 'Market Yard'],
  ['tilak-road', 'Tilak Road'], ['swargate', 'Swargate'],
  ['shankar-seth-road', 'Shankar Seth Road'], ['jm-road', 'JM Road'],
  ['chandni-chowk', 'Chandni Chowk'], ['nigdi', 'Nigdi'], ['akurdi', 'Akurdi'],
  ['pimpri', 'Pimpri'], ['chinchwad', 'Chinchwad'], ['lulla-nagar', 'Lulla Nagar'],
  ['kondhwa', 'Kondhwa'], ['wanowrie', 'Wanowrie'], ['undri', 'Undri'],
  ['dhayari', 'Dhayari'], ['warje', 'Warje'], ['deccan', 'Deccan Gymkhana'],
];

const BHKS = ['2bhk', '3bhk', '4bhk', '5bhk', 'luxury', 'ultra-luxury'];
const BUDGETS = ['1cr', '1.5cr', '2cr', '3cr', '4cr', '5cr', '6cr'];
const BHK_LABELS = { '2bhk': '2 BHK', '3bhk': '3 BHK', '4bhk': '4 BHK', '5bhk': '5 BHK', 'luxury': 'Luxury', 'ultra-luxury': 'Ultra-Luxury' };
const BUDGET_LABELS = { '1cr': '₹1 Cr', '1.5cr': '₹1.5 Cr', '2cr': '₹2 Cr', '3cr': '₹3 Cr', '4cr': '₹4 Cr', '5cr': '₹5 Cr', '6cr': '₹6 Cr' };

// Mirrors backend data/seo_page_definitions.py
const AGENT_AREAS = [
  ['baner', 'Baner'], ['balewadi', 'Balewadi'], ['aundh', 'Aundh'], ['wakad', 'Wakad'],
  ['hinjewadi', 'Hinjewadi'], ['kharadi', 'Kharadi'], ['viman-nagar', 'Viman Nagar'],
  ['kalyani-nagar', 'Kalyani Nagar'], ['koregaon-park', 'Koregaon Park'], ['kothrud', 'Kothrud'],
  ['bavdhan', 'Bavdhan'], ['mahalunge', 'Mahalunge'], ['hadapsar', 'Hadapsar'],
  ['magarpatta', 'Magarpatta'], ['nibm-road', 'NIBM Road'], ['kondhwa', 'Kondhwa'], ['undri', 'Undri'],
];
const RELOCATION_CITIES = [
  ['mumbai', 'Mumbai'], ['bangalore', 'Bangalore'], ['delhi-ncr', 'Delhi NCR'], ['hyderabad', 'Hyderabad'],
  ['chennai', 'Chennai'], ['kolkata', 'Kolkata'], ['ahmedabad', 'Ahmedabad'], ['gurgaon', 'Gurgaon'],
  ['noida', 'Noida'], ['tier-2-maharashtra', 'Tier-2 Maharashtra'],
];
const NRI_COUNTRIES = [
  ['usa', 'USA'], ['uk', 'UK'], ['uae', 'UAE'], ['singapore', 'Singapore'], ['australia', 'Australia'],
  ['canada', 'Canada'], ['germany', 'Germany'], ['qatar', 'Qatar'], ['saudi-arabia', 'Saudi Arabia'],
  ['kuwait', 'Kuwait'], ['oman', 'Oman'], ['bahrain', 'Bahrain'],
];

const Section = ({ title, defaultOpen = false, children, count }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-white/5">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`seo-footer-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-4 text-left group"
      >
        <h3 className="font-outfit text-white/80 text-sm font-semibold flex items-center gap-2">
          {title}
          {count !== undefined && <span className="text-white/30 text-[11px] font-normal">({count})</span>}
        </h3>
        <ChevronDown size={16} className={`text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {/* Always render in DOM (CSS-hidden when collapsed) so Googlebot sees all links — critical for link equity */}
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-[2000px] opacity-100 pb-5' : 'max-h-0 opacity-0'}`}>
        {children}
      </div>
    </div>
  );
};

export const SEOFooterLinks = () => {
  return (
    <section data-testid="seo-footer-links" className="bg-black border-t border-white/10" aria-label="Browse properties by area and budget">
      <div className="container-custom py-4">
        <Section title="Properties by Pune Area" count={AREAS.length}>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1.5">
            {AREAS.map(([slug, label]) => (
              <li key={slug}>
                <Link to={`/properties-in-${slug}`}
                  className="text-white/40 hover:text-brand text-xs font-dm-sans transition-colors inline-block py-0.5">
                  Properties in {label}
                </Link>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Properties by Configuration & Budget" count={BHKS.length * BUDGETS.length}>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1.5">
            {BHKS.flatMap(bhk => BUDGETS.map(b => (
              <li key={`${bhk}-${b}`}>
                <Link to={`/${bhk}-in-pune-under-${b}`}
                  className="text-white/40 hover:text-brand text-xs font-dm-sans transition-colors inline-block py-0.5">
                  {BHK_LABELS[bhk]} under {BUDGET_LABELS[b]}
                </Link>
              </li>
            )))}
          </ul>
        </Section>

        <Section title="Advisors, Relocation & NRI Guides" count={AGENT_AREAS.length + RELOCATION_CITIES.length + NRI_COUNTRIES.length + 5}>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1.5">
            {AGENT_AREAS.map(([slug, label]) => (
              <li key={`agent-${slug}`}>
                <Link to={`/real-estate-agents-${slug}`}
                  className="text-white/40 hover:text-brand text-xs font-dm-sans transition-colors inline-block py-0.5">
                  Agents in {label}
                </Link>
              </li>
            ))}
            <li><Link to="/top-real-estate-agents-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans inline-block py-0.5">Top Agents in Pune</Link></li>
            <li><Link to="/commercial-property-advisors-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans inline-block py-0.5">Commercial Advisors Pune</Link></li>
            <li><Link to="/office-space-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans inline-block py-0.5">Office Space Pune</Link></li>
            <li><Link to="/retail-space-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans inline-block py-0.5">Retail Space Pune</Link></li>
            <li><Link to="/relocating-to-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans inline-block py-0.5">Relocating to Pune</Link></li>
            {RELOCATION_CITIES.map(([slug, label]) => (
              <li key={`reloc-${slug}`}>
                <Link to={`/moving-to-pune-from-${slug}`}
                  className="text-white/40 hover:text-brand text-xs font-dm-sans transition-colors inline-block py-0.5">
                  Moving from {label}
                </Link>
              </li>
            ))}
            {NRI_COUNTRIES.map(([slug, label]) => (
              <li key={`nri-${slug}`}>
                <Link to={`/nri/buy-property-in-pune-from-${slug}`}
                  className="text-white/40 hover:text-brand text-xs font-dm-sans transition-colors inline-block py-0.5">
                  NRI Buying from {label}
                </Link>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Services">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5" data-testid="footer-services-list">
            <li><Link to="/luxury-advisors-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans">Residential Advisory</Link></li>
            <li><Link to="/commercial-property-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans">Commercial Advisory</Link></li>
            <li><Link to="/properties?property_type=Pre-Leased" className="text-white/40 hover:text-brand text-xs font-dm-sans">Pre-Leased Investment Advisory</Link></li>
            <li><Link to="/office-space-pune" className="text-white/40 hover:text-brand text-xs font-dm-sans">Corporate Leasing</Link></li>
            <li><Link to="/nri-property-investment" className="text-white/40 hover:text-brand text-xs font-dm-sans">NRI Advisory</Link></li>
            <li><Link to="/dubai-property-advisory" className="text-white/40 hover:text-brand text-xs font-dm-sans">Dubai Advisory for Indians &amp; NRIs</Link></li>
          </ul>
        </Section>

        <Section title="Tools & Resources">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5" data-testid="footer-tools-list">
            <li><Link to="/compare" className="text-white/40 hover:text-brand text-xs font-dm-sans">AI Property Comparison</Link></li>
            <li><Link to="/emi-calculator" className="text-white/40 hover:text-brand text-xs font-dm-sans">Home Loan EMI Calculator</Link></li>
            <li><Link to="/blog" className="text-white/40 hover:text-brand text-xs font-dm-sans">Pune Real Estate Blog</Link></li>
          </ul>
        </Section>
      </div>
    </section>
  );
};

export default SEOFooterLinks;
